import { FocusScope } from "@tamagui/focus-scope";
import { Portal } from "@tamagui/portal";
import React, { forwardRef, useRef, useState } from "react";
import { Animated, PanResponder } from "react-native-web";
import { AnimatePresence, YStack, createStyledContext, styled, useControllableState, usePropsAndStyle, withStaticProperties } from "tamagui";
import { Fragment, jsx } from "react/jsx-runtime";
const DrawerContext = createStyledContext({
  open: false,
  setOpen: () => {}
});
const SwipeDismissableComponent = React.forwardRef(({
  onDismiss,
  children,
  dismissAfter = 80,
  ...rest
}, ref) => {
  const pan = useRef(new Animated.Value(0)).current;
  const [props, style] = usePropsAndStyle(rest);
  const [dragStarted, setDragStarted] = useState(false);
  const dismissAfterRef = useRef(dismissAfter);
  const panResponder = useRef(PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onPanResponderMove: (event, gestureState) => {
      const {
        dx
      } = gestureState;
      if (dx < 0) {
        setDragStarted(true);
        pan.setValue(dx);
      }
    },
    onPanResponderRelease: (e, gestureState) => {
      setDragStarted(false);
      if (gestureState.dx < -dismissAfterRef.current) {
        if (onDismiss) {
          onDismiss();
        }
      } else {
        Animated.spring(pan, {
          toValue: 0,
          useNativeDriver: true,
          overshootClamping: true
        }).start();
      }
    }
  })).current;
  return /* @__PURE__ */jsx(Animated.View, {
    ref,
    style: {
      transform: [{
        translateX: pan
      }],
      height: "100%",
      ...style,
      ...(dragStarted && {
        pointerEvents: "none"
      })
    },
    ...panResponder.panHandlers,
    ...props,
    children
  });
});
const DrawerFrame = styled(YStack, {
  variants: {
    unstyled: {
      false: {
        themeInverse: true,
        paddingVertical: "$2",
        tag: "nav",
        width: 210,
        alignItems: "flex-start",
        justifyContent: "flex-start",
        backgroundColor: "$background",
        x: 0,
        gap: "$4"
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const Overlay = styled(YStack, {
  name: "DrawerOverlay",
  context: DrawerContext,
  enterStyle: {
    opacity: 0
  },
  exitStyle: {
    opacity: 0
  },
  variants: {
    unstyled: {
      false: {
        fullscreen: true,
        position: "absolute",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1e5 - 1,
        pointerEvents: "auto"
      }
    }
  },
  defaultVariants: {
    unstyled: process.env.TAMAGUI_HEADLESS === "1" ? true : false
  }
});
const DrawerOverlay = Overlay.styleable((props, ref) => {
  const {
    setOpen
  } = DrawerContext.useStyledContext();
  return /* @__PURE__ */jsx(Overlay, {
    ref,
    onPress: () => setOpen(false),
    ...props
  });
});
const DrawerSwipeable = forwardRef((props, ref) => {
  const {
    setOpen,
    open: _open
  } = DrawerContext.useStyledContext();
  return /* @__PURE__ */jsx(SwipeDismissableComponent, {
    onDismiss: () => setOpen(false),
    zIndex: 1e9,
    position: "absolute",
    ...props,
    ref
  });
});
const DrawerContent = DrawerFrame.styleable((props, ref) => {
  const {
    children,
    ...rest
  } = props;
  return /* @__PURE__ */jsx(FocusScope, {
    trapped: true,
    enabled: true,
    loop: true,
    children: /* @__PURE__ */jsx(DrawerFrame, {
      ref,
      transition: "medium",
      enterStyle: {
        x: -(rest.width || rest.w || 210)
      },
      exitStyle: {
        x: -(rest.width || rest.w || 210)
      },
      ...rest,
      children
    })
  });
});
const DrawerImpl = ({
  open = false,
  onOpenChange,
  children,
  portalToRoot,
  ...rest
}) => {
  const [_open, setOpen] = useControllableState({
    prop: open,
    defaultProp: false,
    onChange: onOpenChange
  });
  const content = open && /* @__PURE__ */jsx(Fragment, {
    children
  });
  return /* @__PURE__ */jsx(DrawerContext.Provider, {
    open: _open,
    setOpen,
    children: /* @__PURE__ */jsx(AnimatePresence, {
      children: open && content
    })
  });
};
const DrawerPortal = props => {
  return /* @__PURE__ */jsx(Portal, {
    zIndex: 1e9,
    ...props
  });
};
const Drawer = withStaticProperties(DrawerImpl, {
  Content: DrawerContent,
  Overlay: DrawerOverlay,
  Swipeable: DrawerSwipeable,
  Portal: DrawerPortal
});
export { Drawer, DrawerContext };
//# sourceMappingURL=Drawer.mjs.map
